import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { useCart } from '../state/cart';
import { useT } from '../i18n';
import PayFirstProof from './PayFirstProof';

// Decision point shown right after a pay-first "add to cart": the buyer
// confirms what happens next — pay for everything in the cart now, or go add
// more books first. Centered dialog on desktop, bottom sheet on phones (see
// the .atc-sheet styles). Background scroll is locked while open so the page
// can't drift under the overlay; the X (or tapping the overlay) dismisses and
// stays on the product page, e.g. to add another copy.
export default function AddedToCartSheet({ onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { slots } = useCart();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // The TRUE visible viewport height, measured by JS. CSS units (vh/dvh/
  // position:fixed inset:0) routinely exceed the visible area — mobile URL
  // bars, embedded panes — which pushed the sheet's buttons below the fold.
  // The overlay is pinned to this measured height so the sheet's 100%
  // max-height is exact and the buttons always land on screen.
  const [vpH, setVpH] = useState(() =>
    (typeof window !== 'undefined' ? Math.round(window.visualViewport?.height || window.innerHeight) : 0));
  useEffect(() => {
    const update = () => setVpH(Math.round(window.visualViewport?.height || window.innerHeight));
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return createPortal(
    <div
      className="atc-sheet-overlay"
      style={vpH ? { height: vpH } : undefined}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="atc-sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="atc-sheet-close" onClick={onClose} aria-label={t.close || 'X'}>
          <X size={20} />
        </button>
        <div className="atc-sheet-icon"><Check size={26} strokeWidth={3} aria-hidden="true" /></div>
        <h3 className="atc-sheet-title">{t.payFirstAddedModalTitle}</h3>
        <p className="atc-sheet-sub">{t.payFirstAddedModalSubtitle}</p>
        {/* Real customer conversation, then the decision buttons right under it. */}
        <PayFirstProof />
        <div className="atc-sheet-actions">
          <button type="button" className="submit-btn" onClick={() => navigate('/checkout')}>
            {t.payFirstModalCheckout}{slots.length > 1 ? ` (${slots.length})` : ''}
          </button>
          <button type="button" className="atc-sheet-keep" onClick={() => navigate('/')}>
            {t.payFirstModalKeepShopping}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
